import './styles.css';
import { ShadowingController } from '../../src/core/shadowingController';
import type { ShadowingState, SubtitleCue } from '../../src/core/types';
import { DEFAULT_KEYS } from '../../src/settings/defaults';
import type { PublicCaptionTrack, TranscriptApiResponse } from '../../src/shared/transcriptApi';
import { parseYouTubeInput, type ParsedYouTubeInput } from '../../src/shared/youtubeUrl';
import { isTypingContext } from '../../src/utils/dom';
import { IFramePlayerAdapter } from './IFramePlayerAdapter';
import { loadTranscript, TranscriptApiError } from './TranscriptApiClient';
import { loadYouTubeIframeApi, type YouTubeApi, type YouTubePlayer } from './youtubeIframe';

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}

function formatTime(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = whole % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
    : `${minutes}:${String(rest).padStart(2, '0')}`;
}

class PwaApp {
  private readonly form = required<HTMLFormElement>('#video-form');
  private readonly input = required<HTMLInputElement>('#video-url');
  private readonly inputError = required<HTMLElement>('#url-error');
  private readonly playerStatus = required<HTMLElement>('#player-status');
  private readonly transcriptStatus = required<HTMLElement>('#transcript-status');
  private readonly selectionStatus = required<HTMLElement>('#selection-status');
  private readonly transcriptList = required<HTMLElement>('#transcript-list');
  private readonly trackWrap = required<HTMLElement>('#track-wrap');
  private readonly trackSelect = required<HTMLSelectElement>('#track-select');
  private readonly multiButton = required<HTMLButtonElement>('#multi-toggle');
  private readonly previousButton = required<HTMLButtonElement>('#previous-button');
  private readonly replayButton = required<HTMLButtonElement>('#replay-button');
  private readonly playButton = required<HTMLButtonElement>('#play-button');
  private readonly nextButton = required<HTMLButtonElement>('#next-button');
  private readonly shrinkButton = required<HTMLButtonElement>('#shrink-button');
  private readonly extendButton = required<HTMLButtonElement>('#extend-button');
  private readonly liveRegion = required<HTMLElement>('#live-status');
  private readonly offlineBanner = required<HTMLElement>('#offline-banner');
  private readonly adapter = new IFramePlayerAdapter();

  private api: YouTubeApi | null = null;
  private player: YouTubePlayer | null = null;
  private playerReady = false;
  private transcript: TranscriptApiResponse | null = null;
  private controller: ShadowingController | null = null;
  private unsubscribe: (() => void) | null = null;
  private rows: HTMLButtonElement[] = [];
  private sessionId = 0;
  private currentVideoId: string | null = null;
  private initialSeconds = 0;
  private transcriptAbort: AbortController | null = null;
  private multiMode = false;
  private playerState = -1;
  private lastScrolledIndex: number | null = null;
  private syncTimer: number | null = null;

  start(): void {
    this.input.value = localStorage.getItem('ys:lastUrl') ?? '';
    this.form.addEventListener('submit', this.onSubmit);
    this.transcriptList.addEventListener('click', this.onCueClick);
    this.multiButton.addEventListener('click', this.toggleMultiMode);
    this.previousButton.addEventListener('click', () => this.runAction('previous'));
    this.replayButton.addEventListener('click', () => this.runAction('replay'));
    this.playButton.addEventListener('click', () => this.runAction('togglePlay'));
    this.nextButton.addEventListener('click', () => this.runAction('next'));
    this.shrinkButton.addEventListener('click', () => this.runAction('shrink'));
    this.extendButton.addEventListener('click', () => this.runAction('extend'));
    this.trackSelect.addEventListener('change', () => void this.reloadTranscript(this.trackSelect.value));
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('online', this.updateOnlineState);
    window.addEventListener('offline', this.updateOnlineState);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.updateOnlineState();
    this.renderControls(null);
    this.registerServiceWorker();
  }

  private readonly onSubmit = (event: SubmitEvent): void => {
    event.preventDefault();
    const parsed = parseYouTubeInput(this.input.value);
    if (!parsed) {
      this.inputError.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
      return;
    }
    this.inputError.hidden = true;
    this.input.removeAttribute('aria-invalid');
    localStorage.setItem('ys:lastUrl', this.input.value.trim());
    void this.loadVideo(parsed);
  };

  private async loadVideo(parsed: ParsedYouTubeInput): Promise<void> {
    const token = ++this.sessionId;
    this.currentVideoId = parsed.videoId;
    this.initialSeconds = parsed.initialSeconds;
    this.playerReady = false;
    this.transcript = null;
    this.multiMode = false;
    this.disposeController();
    this.transcriptAbort?.abort();
    this.transcriptAbort = new AbortController();
    this.renderMultiMode();
    this.clearTranscript('正在載入字幕…');
    this.setPlayerStatus('正在準備官方 YouTube 播放器…', 'loading');
    this.renderControls(null);

    const playerPromise = this.preparePlayer(parsed, token);
    const transcriptPromise = this.fetchTranscript(parsed.videoId, token, this.transcriptAbort.signal);
    await Promise.allSettled([playerPromise, transcriptPromise]);
  }

  private async preparePlayer(parsed: ParsedYouTubeInput, token: number): Promise<void> {
    try {
      this.api ??= await loadYouTubeIframeApi();
      if (token !== this.sessionId) return;
      if (!this.player) {
        await new Promise<void>((resolve, reject) => {
          if (!this.api) return reject(new Error('YouTube API unavailable'));
          this.player = new this.api.Player('pwa-player', {
            width: '100%',
            height: '100%',
            playerVars: {
              autoplay: 0,
              controls: 1,
              playsinline: 1,
              fs: 1,
              enablejsapi: 1,
              origin: location.origin,
            },
            events: {
              onReady: (event) => {
                this.adapter.setPlayer(event.target);
                resolve();
              },
              onStateChange: (event) => this.handlePlayerState(event.data),
              onError: (event) => this.handlePlayerError(event.data),
              onAutoplayBlocked: () => this.handleAutoplayBlocked(),
            },
          });
        });
      }
      if (token !== this.sessionId || !this.player) return;
      this.player.cueVideoById({ videoId: parsed.videoId, startSeconds: parsed.initialSeconds });
      this.playerReady = true;
      this.setPlayerStatus('影片已準備完成，請選字幕或按播放。', 'ready');
      this.applyReadySession();
    } catch {
      if (token !== this.sessionId) return;
      this.playerReady = false;
      this.setPlayerStatus('無法載入 YouTube 播放器，請檢查網路或內容阻擋設定。', 'error');
    }
  }

  private async fetchTranscript(videoId: string, token: number, signal: AbortSignal, trackId?: string): Promise<void> {
    try {
      const transcript = await loadTranscript(videoId, { signal, trackId });
      if (token !== this.sessionId) return;
      this.transcript = transcript;
      this.renderTracks(transcript.tracks, transcript.track.id);
      this.renderTranscript(transcript.cues);
      this.transcriptStatus.textContent = `${transcript.track.name}${transcript.track.kind === 'asr' ? '（自動產生）' : ''} · ${transcript.cues.length} 段`;
      this.transcriptStatus.dataset.state = 'ready';
      this.applyReadySession();
    } catch (error) {
      if (token !== this.sessionId || signal.aborted) return;
      this.transcript = null;
      const message = error instanceof TranscriptApiError
        ? this.transcriptProblemMessage(error)
        : '字幕服務暫時無法連線，請稍後重試。';
      this.clearTranscript(message, true);
    }
  }

  private async reloadTranscript(trackId: string): Promise<void> {
    if (!this.currentVideoId) return;
    const token = this.sessionId;
    this.transcriptAbort?.abort();
    this.transcriptAbort = new AbortController();
    this.disposeController();
    this.clearTranscript('正在切換字幕…');
    this.renderControls(null);
    await this.fetchTranscript(this.currentVideoId, token, this.transcriptAbort.signal, trackId);
  }

  private applyReadySession(): void {
    if (!this.playerReady || !this.currentVideoId || !this.transcript) return;
    this.disposeController();
    if (this.initialSeconds > 0) this.adapter.seek(this.initialSeconds);
    this.controller = new ShadowingController(this.adapter, this.currentVideoId);
    this.unsubscribe = this.controller.subscribe((state) => this.renderState(state));
    this.controller.load(this.transcript.cues, this.currentVideoId);
    this.startSyncTimer();
  }

  private renderTranscript(cues: SubtitleCue[]): void {
    this.transcriptList.replaceChildren();
    const fragment = document.createDocumentFragment();
    this.rows = cues.map((cue) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cue-row';
      button.dataset.index = String(cue.index);
      button.setAttribute('role', 'listitem');
      button.setAttribute('aria-label', `字幕 ${cue.index + 1}，${formatTime(cue.start)}：${cue.text}`);
      const marker = document.createElement('span');
      marker.className = 'cue-marker';
      marker.setAttribute('aria-hidden', 'true');
      const body = document.createElement('span');
      body.className = 'cue-body';
      const time = document.createElement('span');
      time.className = 'cue-time';
      time.textContent = formatTime(cue.start);
      const text = document.createElement('span');
      text.className = 'cue-text';
      text.textContent = cue.text;
      body.append(time, text);
      button.append(marker, body);
      fragment.append(button);
      return button;
    });
    this.transcriptList.append(fragment);
    this.transcriptList.hidden = false;
  }

  private renderTracks(tracks: PublicCaptionTrack[], selectedId: string): void {
    this.trackSelect.replaceChildren();
    for (const track of tracks) {
      const option = document.createElement('option');
      option.value = track.id;
      option.textContent = `${track.name}${track.kind === 'asr' ? '（自動）' : ''}`;
      option.selected = track.id === selectedId;
      this.trackSelect.append(option);
    }
    this.trackWrap.hidden = tracks.length <= 1;
  }

  private renderState(state: Readonly<ShadowingState>): void {
    const start = state.segmentStartCueIndex;
    const end = state.segmentEndCueIndex;
    this.rows.forEach((row, index) => {
      const selected = start !== null && end !== null && start !== end && index >= start && index <= end;
      const current = start === end && index === state.currentCueIndex;
      row.classList.toggle('is-selected', selected);
      row.classList.toggle('is-current', current);
      row.querySelector('.cue-marker')!.textContent = selected ? '■' : current ? '▶' : '';
      row.setAttribute('aria-current', current || selected ? 'true' : 'false');
    });
    if (start !== null && end !== null && this.transcript) {
      const startCue = this.transcript.cues[start];
      const endCue = this.transcript.cues[end];
      const cueCount = end - start + 1;
      this.selectionStatus.hidden = false;
      this.selectionStatus.dataset.mode = cueCount > 1 ? 'multi' : 'single';
      this.selectionStatus.textContent = cueCount > 1
        ? `已選第 ${start + 1}–${end + 1} 段 · 共 ${cueCount} 段 · ${formatTime(startCue.start)}–${formatTime(endCue.end)}`
        : `目前第 ${start + 1} 段 · ${formatTime(startCue.start)}–${formatTime(endCue.end)}`;
    } else {
      this.selectionStatus.hidden = true;
    }
    if (state.currentCueIndex !== null && state.currentCueIndex !== this.lastScrolledIndex) {
      this.rows[state.currentCueIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      this.lastScrolledIndex = state.currentCueIndex;
    }
    this.renderControls(state);
  }

  private renderControls(state: Readonly<ShadowingState> | null): void {
    const ready = Boolean(state && this.playerReady && this.transcript?.cues.length);
    const start = state?.segmentStartCueIndex ?? null;
    const end = state?.segmentEndCueIndex ?? null;
    const count = this.transcript?.cues.length ?? 0;
    this.previousButton.disabled = !ready || start === null || start <= 0;
    this.replayButton.disabled = !ready;
    this.playButton.disabled = !ready;
    this.nextButton.disabled = !ready || end === null || end >= count - 1;
    this.shrinkButton.disabled = !ready || start === null || end === null || start === end;
    this.extendButton.disabled = !ready || end === null || end >= count - 1;
    this.multiButton.disabled = !ready;
    const playing = this.playerState === 1 || this.playerState === 3;
    this.playButton.textContent = playing ? '⏸' : '▶';
    this.playButton.setAttribute('aria-label', playing ? '暫停（Space）' : '播放（Space）');
    this.playButton.title = playing ? '暫停（Space）' : '播放（Space）';
  }

  private readonly onCueClick = (event: MouseEvent): void => {
    const row = (event.target as Element).closest<HTMLButtonElement>('.cue-row');
    if (!row || !this.controller) return;
    const index = Number(row.dataset.index);
    if (this.multiMode || event.shiftKey) {
      if (!this.controller.selectThrough(index)) {
        this.announce('多選只能從目前字幕向後連續選取。');
      }
    } else {
      this.controller.selectCue(index);
    }
  };

  private readonly toggleMultiMode = (): void => {
    if (!this.controller) return;
    this.multiMode = !this.multiMode;
    if (!this.multiMode) this.controller.cancelMultiSelection();
    this.renderMultiMode();
    this.announce(this.multiMode ? '已開啟連續多選模式。' : '已關閉多選模式。');
  };

  private renderMultiMode(): void {
    this.multiButton.classList.toggle('is-on', this.multiMode);
    this.multiButton.setAttribute('aria-pressed', String(this.multiMode));
    this.multiButton.querySelector('.switch-state')!.textContent = this.multiMode ? 'ON' : 'OFF';
  }

  private runAction(action: 'previous' | 'replay' | 'togglePlay' | 'next' | 'shrink' | 'extend'): void {
    const controller = this.controller;
    if (!controller) return;
    const handled = controller[action]();
    if (!handled && (action === 'previous' || action === 'next')) {
      this.announce(action === 'previous' ? '已是第一段字幕。' : '已是最後一段字幕。');
    }
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || isTypingContext(event.target)) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    let action: 'previous' | 'replay' | 'togglePlay' | 'next' | 'shrink' | 'extend' | 'cancelMultiSelection' | null = null;
    if (key === DEFAULT_KEYS.previous) action = 'previous';
    else if (key === DEFAULT_KEYS.replay) action = 'replay';
    else if (key === DEFAULT_KEYS.next) action = 'next';
    else if (key === DEFAULT_KEYS.togglePlay) action = 'togglePlay';
    else if (key === DEFAULT_KEYS.shrink) action = 'shrink';
    else if (key === DEFAULT_KEYS.extend) action = 'extend';
    else if (key === DEFAULT_KEYS.cancelSelection) action = 'cancelMultiSelection';
    if (!action || !this.controller) return;
    event.preventDefault();
    if (action === 'cancelMultiSelection') {
      this.multiMode = false;
      this.controller.cancelMultiSelection();
      this.renderMultiMode();
    } else {
      this.controller[action]();
    }
  };

  private handlePlayerState(state: number): void {
    this.playerState = state;
    this.adapter.setState(state);
    if (state === 1) this.controller?.handlePlayerPlaying();
    else if (state === 0 || state === 2) this.controller?.handlePlayerPaused();
    this.renderControls(this.controller?.getState() ?? null);
  }

  private handlePlayerError(code: number): void {
    this.playerReady = false;
    const messages: Record<number, string> = {
      2: '影片網址或 ID 無效。',
      5: 'YouTube HTML5 播放器發生錯誤。',
      100: '影片不存在、已移除或設為私人。',
      101: '影片擁有者不允許在其他網站嵌入播放。',
      150: '影片擁有者不允許在其他網站嵌入播放。',
      153: '播放器缺少網站識別資訊，請檢查部署的 Referrer-Policy。',
    };
    this.setPlayerStatus(messages[code] ?? `YouTube 播放器錯誤（${code}）。`, 'error');
    this.renderControls(null);
  }

  private handleAutoplayBlocked(): void {
    this.controller?.setEnabled(false);
    this.setPlayerStatus('瀏覽器阻擋了自動播放，請點擊播放按鈕開始。', 'error');
    this.announce('瀏覽器阻擋了播放，請手動按播放。');
  }

  private startSyncTimer(): void {
    if (this.syncTimer !== null) window.clearInterval(this.syncTimer);
    this.syncTimer = window.setInterval(() => {
      const controller = this.controller;
      if (!controller || this.playerState !== 1) return;
      const state = controller.getState();
      const segment = controller.getSegment();
      const time = this.adapter.getCurrentTime();
      if (
        state.autoPauseTarget !== null &&
        segment &&
        (time < segment.startTime - 0.3 || time > segment.endTime + 0.5)
      ) {
        this.adapter.pause();
        controller.handleSeeking();
      } else {
        controller.syncCurrentCue(time);
      }
    }, 100);
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'hidden' || !this.controller?.getState().isPlayingSegment) return;
    this.adapter.pause();
    this.controller.setEnabled(false);
    this.announce('切到背景時已暫停目前段落。');
  };

  private disposeController(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.controller?.dispose();
    this.controller = null;
    this.lastScrolledIndex = null;
    if (this.syncTimer !== null) window.clearInterval(this.syncTimer);
    this.syncTimer = null;
  }

  private clearTranscript(message: string, error = false): void {
    this.transcriptList.replaceChildren();
    this.transcriptList.hidden = true;
    this.rows = [];
    this.selectionStatus.hidden = true;
    this.trackWrap.hidden = true;
    this.transcriptStatus.textContent = message;
    this.transcriptStatus.dataset.state = error ? 'error' : 'loading';
  }

  private setPlayerStatus(message: string, state: 'loading' | 'ready' | 'error'): void {
    this.playerStatus.textContent = message;
    this.playerStatus.dataset.state = state;
  }

  private transcriptProblemMessage(error: TranscriptApiError): string {
    switch (error.problem.code) {
      case 'TRANSCRIPT_DISABLED':
      case 'TRANSCRIPT_NOT_FOUND':
        return '此影片沒有可取得的字幕。';
      case 'RATE_LIMITED':
        return '字幕服務目前請求過多，請稍後再試。';
      case 'TRANSCRIPT_PARSE_FAILED':
        return 'YouTube 字幕格式已變更，字幕服務暫時不相容。';
      default:
        return `字幕載入失敗：${error.problem.detail}`;
    }
  }

  private announce(message: string): void {
    this.liveRegion.textContent = '';
    window.setTimeout(() => { this.liveRegion.textContent = message; }, 10);
  }

  private readonly updateOnlineState = (): void => {
    this.offlineBanner.hidden = navigator.onLine;
  };

  private registerServiceWorker(): void {
    if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;
    void navigator.serviceWorker.register('/sw.js');
  }
}

new PwaApp().start();
